import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button, Card, CardBody, ConfirmDialog, EmptyState, ErrorState, Input, Select, SkeletonCard } from "@/components/ui";
import { useToast } from "@/components/ui/Toast";
import {
  useHomeBlocks,
  useCreateHomeBlock,
  useUpdateHomeBlock,
  useReorderHomeBlocks,
  usePublishHomeBlock,
  useUnpublishHomeBlock,
  useDeleteHomeBlock,
  useBulkDeleteHomeBlocks,
} from "@/lib/queries";
import { HomeBlockRow } from "@/components/homeBlocks/HomeBlockRow";
import { HomeBlockFormDrawer } from "@/components/homeBlocks/HomeBlockFormDrawer";
import { HomeBlockPreviewModal } from "@/components/homeBlocks/HomeBlockPreviewModal";
import { isSystemBlock } from "@/lib/utils/homeBlockGroups";
import type { HomeBlock, HomeBlockEffectiveStatus, HomeBlockInput } from "@/lib/api";

type OriginFilter = "all" | "system" | "custom";
type StatusFilter = "all" | HomeBlockEffectiveStatus;

export function HomeBlocksPage() {
  const blocksQuery = useHomeBlocks();
  const createBlock = useCreateHomeBlock();
  const updateBlock = useUpdateHomeBlock();
  const reorderBlocks = useReorderHomeBlocks();
  const publishBlock = usePublishHomeBlock();
  const unpublishBlock = useUnpublishHomeBlock();
  const deleteBlock = useDeleteHomeBlock();
  const bulkDeleteBlocks = useBulkDeleteHomeBlocks();
  const { toast } = useToast();

  const [items, setItems] = useState<HomeBlock[]>([]);
  const [query, setQuery] = useState("");
  const [originFilter, setOriginFilter] = useState<OriginFilter>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleteTarget, setDeleteTarget] = useState<HomeBlock | null>(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingBlock, setEditingBlock] = useState<HomeBlock | null>(null);
  const [previewBlock, setPreviewBlock] = useState<HomeBlock | null>(null);
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (blocksQuery.data) {
      setItems([...blocksQuery.data].sort((a, b) => a.order - b.order));
    }
  }, [blocksQuery.data]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const normalizedQuery = query.trim().toLowerCase();
  const filteredItems = useMemo(() => {
    return items.filter((block) => {
      if (originFilter !== "all" && block.source !== originFilter) return false;
      if (statusFilter !== "all" && block.effectiveStatus !== statusFilter) return false;
      if (!normalizedQuery) return true;
      return [block.label, block.title, block.id]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(normalizedQuery));
    });
  }, [items, normalizedQuery, originFilter, statusFilter]);

  const filtering = normalizedQuery.length > 0 || originFilter !== "all" || statusFilter !== "all";
  const reorderViewItems = filtering ? filteredItems : items;
  const selectedCustomBlocks = filteredItems.filter((block) => selectedIds.has(block.id) && !isSystemBlock(block));

  async function commitReorder(reordered: HomeBlock[]) {
    const withOrder = reordered.map((block, index) => ({ ...block, order: (index + 1) * 10 }));
    const previous = items;
    setItems(withOrder);

    try {
      await reorderBlocks.mutateAsync(withOrder.map((b) => ({ id: b.id, order: b.order })));
    } catch {
      setItems(previous);
      toast({ title: "No se pudo reordenar", description: "Se ha restaurado el orden anterior.", variant: "error" });
    }
  }

  async function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((b) => b.id === active.id);
    const newIndex = items.findIndex((b) => b.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    await commitReorder(arrayMove(items, oldIndex, newIndex));
  }

  /** Keyboard/touch-friendly alternative to drag-and-drop - always available on mobile. */
  async function handleMove(index: number, direction: -1 | 1) {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= items.length) return;
    await commitReorder(arrayMove(items, index, targetIndex));
  }

  async function handleDelete(block: HomeBlock) {
    await deleteBlock.mutateAsync(block.id);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(block.id);
      return next;
    });
    toast({ title: "Bloque eliminado", description: "Los envios pendientes vinculados se cancelaron.", variant: "success" });
  }

  async function handleBulkDelete() {
    const ids = selectedCustomBlocks.map((block) => block.id);
    await bulkDeleteBlocks.mutateAsync(ids);
    setSelectedIds(new Set());
    toast({ title: "Bloques eliminados", description: `${ids.length} bloques personalizados eliminados.`, variant: "success" });
  }

  async function withBusy(id: string, action: () => Promise<void>) {
    setBusyIds((prev) => new Set(prev).add(id));
    try {
      await action();
    } catch {
      toast({ title: "No se pudo completar la accion", variant: "error" });
    } finally {
      setBusyIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }

  function handleToggleEnabled(block: HomeBlock, enabled: boolean) {
    void withBusy(block.id, async () => {
      await updateBlock.mutateAsync({
        id: block.id,
        payload: { ...blockToInput(block), enabled },
      });
      toast({ title: enabled ? "Bloque activado" : "Bloque desactivado", variant: "success" });
    });
  }

  function handleTogglePublish(block: HomeBlock) {
    void withBusy(block.id, async () => {
      if (block.status === "published") {
        await unpublishBlock.mutateAsync(block.id);
        toast({ title: "Bloque movido a borrador", variant: "info" });
      } else {
        await publishBlock.mutateAsync(block.id);
        toast({ title: "Bloque publicado", variant: "success" });
      }
    });
  }

  async function handleFormSubmit(payload: HomeBlockInput) {
    if (editingBlock) {
      await updateBlock.mutateAsync({ id: editingBlock.id, payload });
      toast({ title: "Bloque actualizado", variant: "success" });
    } else {
      await createBlock.mutateAsync(payload);
      toast({ title: "Bloque creado como borrador", variant: "success" });
    }
  }

  const nextOrder = items.length > 0 ? (items[items.length - 1]?.order ?? 0) + 10 : 10;
  const systemCount = items.filter(isSystemBlock).length;
  const customCount = items.length - systemCount;
  const allVisibleCustomSelected =
    filteredItems.some((block) => !isSystemBlock(block)) &&
    filteredItems.filter((block) => !isSystemBlock(block)).every((block) => selectedIds.has(block.id));

  return (
    <div>
      <PageHeader
        title="Inicio"
        description="Ordena y publica los bloques dinamicos de la pantalla de Inicio de IESAPP."
        actions={
          <Button
            icon={<Plus className="h-4 w-4" aria-hidden="true" />}
            onClick={() => {
              setEditingBlock(null);
              setFormOpen(true);
            }}
          >
            Nuevo bloque
          </Button>
        }
      />

      {blocksQuery.isLoading && (
        <div className="space-y-3">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      )}

      {blocksQuery.isError && (
        <Card>
          <CardBody>
            <ErrorState description="No se pudieron cargar los bloques de Inicio." onRetry={() => blocksQuery.refetch()} />
          </CardBody>
        </Card>
      )}

      {blocksQuery.isSuccess && items.length === 0 && (
        <Card>
          <CardBody>
            <EmptyState
              title="Todavia no hay bloques configurados"
              description="No hay bloques del sistema ni personalizados devueltos por el backend en esta instancia."
              action={
                <Button size="sm" onClick={() => setFormOpen(true)}>
                  Nuevo bloque
                </Button>
              }
            />
          </CardBody>
        </Card>
      )}

      {items.length > 0 && (
        <>
          <Card className="mb-4">
            <CardBody>
              <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_180px_210px_auto] lg:items-end">
                <label htmlFor="home-block-search" className="space-y-1 text-sm font-medium text-ink">
                  Buscar bloques
                  <Input
                    id="home-block-search"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Buscar bloques..."
                  />
                </label>
                <label htmlFor="home-block-origin-filter" className="space-y-1 text-sm font-medium text-ink">
                  Origen
                  <Select id="home-block-origin-filter" value={originFilter} onChange={(event) => setOriginFilter(event.target.value as OriginFilter)}>
                    <option value="all">Todos</option>
                    <option value="system">Sistema</option>
                    <option value="custom">Personalizados</option>
                  </Select>
                </label>
                <label htmlFor="home-block-status-filter" className="space-y-1 text-sm font-medium text-ink">
                  Estado
                  <Select id="home-block-status-filter" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}>
                    <option value="all">Todos los estados</option>
                    <option value="draft">Borrador</option>
                    <option value="scheduled">Programado</option>
                    <option value="active">Activo</option>
                    <option value="finished">Finalizado</option>
                    <option value="disabled">Deshabilitado</option>
                  </Select>
                </label>
                <Button
                  variant="danger"
                  icon={<Trash2 className="h-4 w-4" aria-hidden="true" />}
                  disabled={selectedCustomBlocks.length === 0}
                  onClick={() => setBulkDeleteOpen(true)}
                >
                  Eliminar seleccionados
                </Button>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-ink-faint">
                <span>Mostrando {filteredItems.length} de {items.length}</span>
                {filtering ? <span>Reordenacion deshabilitada mientras haya filtros activos.</span> : null}
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={allVisibleCustomSelected}
                    onChange={(event) => {
                      setSelectedIds((prev) => {
                        const next = new Set(prev);
                        for (const block of filteredItems) {
                          if (isSystemBlock(block)) continue;
                          if (event.target.checked) next.add(block.id);
                          else next.delete(block.id);
                        }
                        return next;
                      });
                    }}
                  />
                  Seleccionar personalizados visibles
                </label>
              </div>
            </CardBody>
          </Card>
          <p className="mb-3 text-xs text-ink-faint">
            <span className="font-medium text-ink-muted">{systemCount}</span> bloques del sistema
            {" · "}
            <span className="font-medium text-ink-muted">{customCount}</span> personalizados. Arrastra
            con el asa o usa las flechas para reordenar; en movil, usa siempre las flechas.
          </p>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={filtering ? undefined : handleDragEnd}>
            <SortableContext items={reorderViewItems.map((b) => b.id)} strategy={verticalListSortingStrategy}>
              <ul className="space-y-3">
                {filteredItems.map((block, index) => (
                  <HomeBlockRow
                    key={block.id}
                    block={block}
                    position={index}
                    total={filteredItems.length}
                    busy={busyIds.has(block.id)}
                    reordering={reorderBlocks.isPending}
                    onEdit={() => {
                      setEditingBlock(block);
                      setFormOpen(true);
                    }}
                    onPreview={() => setPreviewBlock(block)}
                    onToggleEnabled={(enabled) => handleToggleEnabled(block, enabled)}
                    onTogglePublish={() => handleTogglePublish(block)}
                    onCreateNotification={() => {
                      const params = new URLSearchParams({
                        targetType: "home_block",
                        targetId: block.id,
                        title: block.label || block.title || block.id,
                      });
                      window.location.href = `/notifications?${params.toString()}`;
                    }}
                    onDelete={() => setDeleteTarget(block)}
                    selected={selectedIds.has(block.id)}
                    onSelect={(selected) => {
                      setSelectedIds((prev) => {
                        const next = new Set(prev);
                        if (selected) next.add(block.id);
                        else next.delete(block.id);
                        return next;
                      });
                    }}
                    reorderDisabled={filtering}
                    onMoveUp={() => void handleMove(index, -1)}
                    onMoveDown={() => void handleMove(index, 1)}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        </>
      )}

      <HomeBlockFormDrawer
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={handleFormSubmit}
        editingBlock={editingBlock}
        nextOrder={nextOrder}
      />

      <HomeBlockPreviewModal block={previewBlock} onClose={() => setPreviewBlock(null)} />

      <ConfirmDialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget ? handleDelete(deleteTarget) : undefined}
        title="Eliminar este bloque"
        description={`¿Eliminar este bloque? ${deleteTarget?.title ?? deleteTarget?.label ?? deleteTarget?.id ?? ""}. Esta accion retirara definitivamente el bloque del panel y de IESAPP.`}
        confirmLabel="Eliminar"
        variant="danger"
      />
      <ConfirmDialog
        open={bulkDeleteOpen}
        onClose={() => setBulkDeleteOpen(false)}
        onConfirm={handleBulkDelete}
        title="Eliminar bloques personalizados"
        description={`¿Eliminar ${selectedCustomBlocks.length} bloques personalizados? Esta accion retirara definitivamente los bloques del panel y de IESAPP.`}
        confirmLabel="Eliminar seleccionados"
        variant="danger"
      />
    </div>
  );
}

function blockToInput(block: HomeBlock): HomeBlockInput {
  return {
    type: block.type,
    enabled: block.enabled,
    order: block.order,
    title: block.title,
    subtitle: block.subtitle,
    body: block.body,
    imageUrl: block.imageUrl,
    actionLabel: block.actionLabel,
    actionTarget: block.actionTarget,
    startsAt: block.startsAt,
    endsAt: block.endsAt,
    status: block.status,
    metadata: block.metadata,
  };
}
